import { type QueryKey, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { isObject, isUndefined } from 'lodash';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type Board, type Clip, ViewTypeName } from '@air/api/types';
import { type Upload } from '@air/redux-uploader';

import { GALLERY_ASSETS, GALLERY_BOARDS, GALLERY_FILES, getPrivateSearchTotalKey } from '~/constants/react-query-keys';
import { useSearchParams } from '~/hooks/search/useSearchParams';
import { useRegisterClipEvents } from '~/hooks/useRegisterClipEvents';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';
import { uploadsForCurrentBoardSelector } from '~/store/centralizedBoard/selectors';
import {
  currentSortFieldDirectionSelector,
  currentSortFieldNameSelector,
  currentViewTypeNameSelector,
} from '~/store/configViews/selectors';
import { useGalleryViewItems } from '~/swr-hooks/gallery/galleryView/useGalleryViewItems';
import { usePrivateBoardsFetcher } from '~/swr-hooks/gallery/galleryView/usePrivateBoardsFetcher';
import {
  galleryViewDataToUpdateMediaPlayer,
  getAssetFromAssetsData,
  privateClipsFetcher,
  privateFilesFetcher,
} from '~/swr-hooks/gallery/galleryView/utils';
import { defaultAssetsData, defaultBoardsData, type UseGalleryViewItemsReturn } from '~/swr-hooks/gallery/types';
import { useCanShowAssets, useCanShowBoards, useCanShowFiles } from '~/swr-hooks/gallery/useCanShow';
import { usePrivateMediaPlayerDataUpdate } from '~/swr-hooks/mediaPlayer/usePrivateMediaPlayerData';
import { type UseBoardSearchParamsReturn, useBoardSearchParams } from '~/swr-hooks/search/useBoardSearchParams';
import { type UseClipSearchParamsReturn, useClipSearchParams } from '~/swr-hooks/search/useClipSearchParams';
import { type SearchTotals } from '~/types/SearchTotals';
import { useAirSelector } from '~/utils/ReduxUtils';

export type UsePrivateGalleryViewItemsProps = {
  shouldUpdateMediaPlayer?: boolean;
  showBoards: boolean;
  boardsExpanded: boolean;
  assetsExpanded: boolean;
  showUploads: boolean;
  semantic?: boolean;
  hybrid?: boolean;
  includeCoverImage?: boolean;
  includeDirectDescendantCount?: boolean;
  includeDirectDescendantBoardCount?: boolean;
};

export type UsePrivateGalleryViewItemsReturn = {
  data: UseGalleryViewItemsReturn<Board, Clip>['data'] & { uploads: Upload[] };
} & UseGalleryViewItemsReturn<Board, Clip>;

const emptyUploadsArray: Upload[] = [];

export type PrivateGalleryViewItemsCacheKey = [typeof GALLERY_BOARDS, PrivateGalleryViewItemsCacheParams];

export type PrivateGalleryViewItemsCacheParams = UseClipSearchParamsReturn &
  UseBoardSearchParamsReturn & {
    canShowBoards: boolean;
    canShowFiles: boolean;
    canShowAssets: boolean;
    sortFieldName: string;
    sortDirection: string;
    basePathName: string;
    currentWorkspaceId: string | undefined;
  };

export const isPrivateGalleryViewItemsQueryKey = (key: QueryKey): key is PrivateGalleryViewItemsCacheKey => {
  return Array.isArray(key) && key[0] === GALLERY_BOARDS && isObject(key[1]);
};

export function usePrivateGalleryViewItems({
  shouldUpdateMediaPlayer = true,
  showBoards,
  boardsExpanded,
  assetsExpanded,
  showUploads = true,
  semantic = false,
  hybrid = false,
  includeCoverImage,
  includeDirectDescendantCount,
  includeDirectDescendantBoardCount,
}: UsePrivateGalleryViewItemsProps): UsePrivateGalleryViewItemsReturn {
  const { privateBoardsFetcher } = usePrivateBoardsFetcher();
  const { updatePrivateMediaPlayerData } = usePrivateMediaPlayerDataUpdate();
  const sortFieldName = useSelector(currentSortFieldNameSelector);
  const sortDirection = useSelector(currentSortFieldDirectionSelector);
  const clipsFetchParams = useClipSearchParams({ semantic, hybrid });
  const boardsFetchParams = useBoardSearchParams({
    includeCoverImage,
    includeDirectDescendantCount,
    includeDirectDescendantBoardCount,
  });
  const currentViewTypeName = useSelector(currentViewTypeNameSelector);
  const { pathname } = useRouter();
  const basePathName = pathname.split('/')[1];

  const { search } = useSearchParams();
  const { searchType } = usePrivateSearchContext();
  const nonAssetsEnabled = useMemo(
    () => searchType === 'keyword' || (searchType === 'smart' && isUndefined(search)),
    [searchType, search],
  );

  const uploads = useAirSelector(showUploads ? uploadsForCurrentBoardSelector : () => emptyUploadsArray);
  const { canShowAssets } = useCanShowAssets();
  const { canShowFiles } = useCanShowFiles();
  const { currentWorkspace } = useCurrentWorkspace();
  const queryClient = useQueryClient();

  /**
   * The reason there are 2 "show boards" checks is because _canShowBoards is used to determine if we should
   * show boards from user-applied logic (filters, flattened view, etc.). showBoards comes from higher up
   * (at the page level - like in AllContent) and is a boolean we (the product) use to determine if we should
   * show boards.
   */
  const { canShowBoards: _canShowBoards } = useCanShowBoards();
  const canShowBoards = showBoards && _canShowBoards;

  const dataCacheParams = useMemo<PrivateGalleryViewItemsCacheParams>(() => {
    return {
      ...clipsFetchParams,
      ...boardsFetchParams,
      assetsExpanded,
      basePathName,
      boardsExpanded,
      canShowAssets,
      canShowBoards,
      canShowFiles,
      currentWorkspaceId: currentWorkspace?.id,
      sortDirection,
      sortFieldName,
    };
  }, [
    basePathName,
    clipsFetchParams,
    boardsFetchParams,
    canShowBoards,
    canShowFiles,
    canShowAssets,
    sortFieldName,
    sortDirection,
    currentWorkspace?.id,
    assetsExpanded,
    boardsExpanded,
  ]);

  const {
    isLoading: areBoardsLoading,
    data: boardsData,
    fetchNextPage: loadNextBoardPage,
    isFetchingNextPage: areBoardsValidating,
    hasNextPage: hasMoreBoards,
  } = useInfiniteQuery({
    queryKey: [GALLERY_BOARDS, dataCacheParams],
    queryFn: canShowBoards
      ? ({ pageParam }) =>
          privateBoardsFetcher({
            params: boardsFetchParams,
            sortFieldName,
            sortDirection,
            cursor: pageParam || null,
          })
      : () => defaultBoardsData,
    enabled: ViewTypeName.gallery === currentViewTypeName && !!currentWorkspace?.id && nonAssetsEnabled,
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor,
    initialPageParam: '',
  });

  const assetsParams = {
    params: clipsFetchParams,
    sortDirection,
    sortFieldName,
  };

  const areAllBoardsLoaded =
    !!boardsData && !!currentViewTypeName && (!canShowBoards || !hasMoreBoards || !boardsExpanded);

  const {
    data: clipsData,
    fetchNextPage: loadNextClipsPage,
    isFetchingNextPage: areClipsValidating,
    isLoading: areClipsLoading,
    hasNextPage: hasMoreClips,
  } = useInfiniteQuery({
    queryKey: [GALLERY_ASSETS, dataCacheParams],
    queryFn:
      canShowAssets && currentWorkspace
        ? ({ pageParam }) =>
            privateClipsFetcher({
              ...assetsParams,
              cursor: pageParam || null,
              workspaceId: currentWorkspace.id,
            })
        : () => defaultAssetsData,
    enabled: ({ state: { data } }) => {
      return !data || (areAllBoardsLoaded && !!currentWorkspace?.id);
    },
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor,
    initialPageParam: '',
  });

  const areAllClipsLoaded = !!clipsData && (!canShowAssets || !hasMoreClips || !assetsExpanded);

  useRegisterClipEvents({
    clips: getAssetFromAssetsData(clipsData?.pages),
    workspaceId: currentWorkspace?.id,
  });

  const {
    data: filesData,
    fetchNextPage: loadNextFilesPage,
    isFetchingNextPage: areFilesValidating,
    isLoading: areFilesLoading,
    hasNextPage: hasMoreFiles,
  } = useInfiniteQuery({
    queryKey: [GALLERY_FILES, dataCacheParams],
    queryFn:
      canShowFiles && currentWorkspace
        ? ({ pageParam }) =>
            privateFilesFetcher({
              ...assetsParams,
              cursor: pageParam || null,
              workspaceId: currentWorkspace.id,
            })
        : () => defaultAssetsData,
    enabled: ({ state: { data } }) =>
      (!data && nonAssetsEnabled) ||
      (areAllBoardsLoaded && areAllClipsLoaded && !!currentWorkspace?.id && nonAssetsEnabled),
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor,
    initialPageParam: '',
  });

  const itemsData = useGalleryViewItems({
    boards: {
      isValidating: areBoardsLoading || areBoardsValidating || !currentViewTypeName,
      data: boardsData?.pages,
      loadNextPage: loadNextBoardPage,
      isVisible: canShowBoards,
      hasMore: !!hasMoreBoards,
    },
    clips: {
      isValidating: areClipsLoading || areClipsValidating || !currentViewTypeName,
      data: clipsData?.pages,
      loadNextPage: loadNextClipsPage,
      isVisible: canShowAssets,
      hasMore: !!hasMoreClips,
    },
    files: {
      isValidating: areFilesValidating || areFilesLoading || !currentViewTypeName,
      data: filesData?.pages || [],
      loadNextPage: loadNextFilesPage,
      isVisible: canShowFiles,
      hasMore: !!hasMoreFiles,
    },
  });

  useEffect(() => {
    if (shouldUpdateMediaPlayer) {
      updatePrivateMediaPlayerData(galleryViewDataToUpdateMediaPlayer(itemsData));
    }
  }, [itemsData, updatePrivateMediaPlayerData, shouldUpdateMediaPlayer]);

  useEffect(() => {
    queryClient.setQueryData<SearchTotals>(getPrivateSearchTotalKey(currentWorkspace?.id), {
      total: itemsData.data.boards.total + itemsData.data.clips.total + itemsData.data.files.total,
    });
  }, [itemsData, queryClient, currentWorkspace?.id]);

  return useMemo(
    () => ({
      ...itemsData,
      data: {
        ...itemsData.data,
        uploads,
      },
      isEmpty: itemsData.isEmpty && !uploads.length,
    }),
    [itemsData, uploads],
  );
}
